import React, { useEffect, useState } from "react";
import { Row, Col, Dropdown } from "react-bootstrap";
import { FaUserCircle } from "react-icons/fa";
import { API } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.tsx";
import styles from "./style.tsx";

interface HeaderProps {
  onReset: () => void;
}

// Custom toggle with no default Bootstrap styling, so logged-in and logged-out
// icons render at the same pixel position.
const IconToggle = React.forwardRef<HTMLButtonElement, any>(({ children, onClick }, ref) => (
  <button
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    style={{
      background: "none",
      border: "none",
      padding: 0,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      gap: 6,
      cursor: "pointer",
    }}
  >
    {children}
  </button>
));

const Header: React.FC<HeaderProps> = ({ onReset }) => {
  const [metrics, setMetrics] = useState({ visitors: 0, savings: 0.0 });
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const result = await API.graphql({
          query: `query GetMVPMetrics {
            getMVPMetrics(mvp_id: "travelsnitch") {
              visitors
              savings
            }
          }`,
        });
        const data = (result as any).data.getMVPMetrics;
        setMetrics({ visitors: data.visitors, savings: data.savings });
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };

    fetchMetrics();
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const authControl = isAuthenticated ? (
    <Dropdown align="end">
      <Dropdown.Toggle as={IconToggle} id="user-menu">
        <FaUserCircle size={28} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Header style={{ fontSize: "0.8rem" }}>{user?.email}</Dropdown.Header>
        <Dropdown.Divider />
        <Dropdown.Item onClick={() => navigate("/my-snitches")}>My Snitches</Dropdown.Item>
        <Dropdown.Item onClick={handleSignOut}>Sign Out</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  ) : (
    <button
      onClick={() => navigate("/login")}
      aria-label="Log in"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        color: "#fff",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <FaUserCircle size={28} />
    </button>
  );

  // Auth UI is built but hidden from end users for now. Flip to true to re-enable.
  const SHOW_AUTH_UI = false;

  return (
    <>
      {SHOW_AUTH_UI && (
        /* Top bar with auth icon pinned right */
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingTop: 8,
            paddingBottom: 4,
            minHeight: 32,
          }}
        >
          {authControl}
        </div>
      )}

      <Row className="align-items-center pb-3 flex-column flex-md-row">
        {/* Title */}
        <Col xs="12" className="text-center mb-2">
          <h1
            onClick={onReset}
            style={{
              ...styles.headerTitle,
              fontSize: "1.8rem",
              marginBottom: "0.3rem",
              cursor: "pointer",
            }}
          >
            Travel Snitch
          </h1>
          <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.8)", margin: 0 }}>
            Turn Your Travel Frustrations Into $$$ — Instantly.
          </p>
        </Col>

        {/* Metrics */}
        <Col xs="12" md="12" className="text-center mt-1 mb-2">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "100px",
              padding: "5px 12px",
              fontSize: "0.72rem",
              whiteSpace: "nowrap",
              color: "rgba(255,255,255,0.9)",
              fontWeight: 600,
              letterSpacing: "0.02em",
            }}
          >
            <span>🧳 {metrics.visitors.toLocaleString()} complaints filed</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>💰 ${metrics.savings.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} recovered</span>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Header;
